import { useContext, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Autocomplete, TextField, Typography, Box } from "@mui/material";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import {
  setActiveModel,
  setAiMessageStatus,
  setCurrentConversation,
} from "modules/chat/store/conversationList";
import { setBreadCrumbs, setHeader } from "core/store/shared/header";
import {
  connectSocket,
  disconnectSocket,
  initSocket,
} from "../services/chatSocketService";
import { SidebarContext } from "core/contexts/sidebar/sidebar";
import { useAppTheme } from "core/contexts/theme/theme";
import { PreviewProvider } from "../store/previewContext";
import { MainChatArea } from "./mainChatArea";
import PreviewPane from "./previewPane";
import { allowedExtensions, allowedMimeTypes } from "../../../core/utils/filetype"

interface AssignedToDropDownProps {
  models: string[];
  handleModelSelect: (model: string) => void;
  selectedModel: string;
}

interface AssignedToDropDownProps2 {
  models: any;
  handleModelSelect: (model: string) => void;
  selectedModel: string;
}

export function ModelDropDown({
  models,
  handleModelSelect,
  selectedModel,
}: AssignedToDropDownProps) {
  if (!models || models.length === 0) {
    return <></>;
  }

  const uniqueModels = Array.from(new Set(models));

  function handleSelectModel(modelName: string | null) {
    const selected = modelName || "";
    handleModelSelect(selected);
  }

  return (
    <div className="d-flex flex-row align-items-center align-self-end">
      <Autocomplete
        disablePortal
        options={uniqueModels.sort((a, b) => a.localeCompare(b))}
        value={selectedModel}
        onChange={(_, newValue) => handleSelectModel(newValue)}
        inputValue={selectedModel}
        onInputChange={(_, newInputValue) => handleModelSelect(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{ "& fieldset": { border: "none", width: "100% !important" } }}
            placeholder="Select Model"
          />
        )}
      />
    </div>
  );
}

interface AssignedToDropDownProps {
  models: string[];
  modelDescriptions?: Record<string, string>; // Map of model names to descriptions
  handleModelSelect: (model: string) => void;
  selectedModel: string;
}

export function ModelDropDown2({
  models,
  modelDescriptions = {}, // Default to empty object if not provided
  handleModelSelect,
  selectedModel,
}: AssignedToDropDownProps) {
  if (!models || models.length === 0) {
    return <></>;
  }

  const uniqueModels = Array.from(new Set(models));

  function handleSelectModel(modelName: string | null) {
    const selected = modelName || "";
    handleModelSelect(selected);
  }

  // Get description for selected model to display below dropdown
  const selectedModelDescription = selectedModel
    ? modelDescriptions[selectedModel]
    : "";

  return (
    <div className="d-flex flex-column w-100">
      <div className="d-flex flex-row align-items-center align-self-end w-100">
        <Autocomplete
          disablePortal
          options={uniqueModels.sort((a, b) => a.localeCompare(b))}
          value={selectedModel}
          onChange={(_, newValue) => handleSelectModel(newValue)}
          inputValue={selectedModel}
          onInputChange={(_, newInputValue) => handleModelSelect(newInputValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                "& fieldset": { border: "none", width: "100% !important" },
              }}
              placeholder="Select Model"
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                <Typography variant="body1">{option}</Typography>
                {modelDescriptions[option] && (
                  <Typography variant="caption" color="text.secondary">
                    {modelDescriptions[option]}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          ListboxProps={{
            sx: {
              maxHeight: "300px",
              "& li": {
                padding: "8px 16px",
                borderBottom: "1px solid rgba(0,0,0,0.05)",
              },
            },
          }}
        />
      </div>

      {/* Display selected model description below the dropdown */}
      {selectedModelDescription && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 1,
            px: 1,
            py: 0.5,
            backgroundColor: "rgba(0,0,0,0.03)",
            borderRadius: 1,
          }}
        >
          {selectedModelDescription}
        </Typography>
      )}
    </div>
  );
}

export default function Chat() {
  // Context States
  const [showCompleteHistory, setShowCompleteHistory] = useState(false);

  // Redux States
  const convoState = useAppSelector((state) => state.chatConversations);
  const headerState = useAppSelector((state) => state.header);
  const dispatch = useAppDispatch();

  const { sidebarActiveState, modelData } = useContext(SidebarContext);

  // Local State
  const [socket, setSocket] = useState<any>();

  // Set the header name and breadcrumbs when
  useEffect(() => {
    if (headerState.crumbs.length === 0) {
      dispatch(
        setBreadCrumbs([
          { displayText: "SecAI Chat", path: "" },
          { displayText: "SecAI Routes", path: "" },
        ])
      );
    }
  }, [headerState.crumbs]);

  /**
   * If a tab is selected in the navigation, start a fresh conversation
   */
  useEffect(() => {
    if (sidebarActiveState) {
      handleStartNewConversation();
    }
  }, [sidebarActiveState]);

  /**
   * If the current conversation === "", a new conversation is made on the next message
   */
  const handleStartNewConversation = () => {
    // Reset the load status for messages if one is currently in progress
    dispatch(setAiMessageStatus("init"));

    // Set the current conversation to "", the next message will create a new conversation obj
    dispatch(setCurrentConversation(""));
  };

  useEffect(() => {
    setSocket(initSocket());
    connectSocket();
    handleStartNewConversation();

    return () => {
      if (socket) {
        disconnectSocket();
      }
    };
  }, []);

  /**
   * Update the selected provider (model), when passed through the parameters of the url
   */
  function findObjectByParameter(data, targetValue) {
    // Handle the case when data is an array (like the root sidebarData)
    if (Array.isArray(data)) {
      for (const item of data) {
        const result = findObjectByParameter(item, targetValue);
        if (result) return result;
      }
      return null;
    }

    // Handle the case when data is an object
    if (data && typeof data === "object") {
      // Check if this object has the parameter matching the target value
      if (data.parameter === targetValue) {
        return data;
      }

      // Search through all properties of the object
      for (const key in data) {
        // Skip if the property is not an object or array
        if (typeof data[key] !== "object" || data[key] === null) {
          continue;
        }

        const result = findObjectByParameter(data[key], targetValue);
        if (result) return result;
      }
    }

    // If we get here, no match was found
    return null;
  }

  function setHeaderName(sidebarObj, nice_name) {
    if (sidebarObj && !sidebarObj.sidebarProps.useNiceName) {
      if (sidebarObj.sidebarProps.customPrefix) {
        dispatch(
          setHeader(
            `${sidebarObj.sidebarProps.customPrefix}${sidebarObj.sidebarProps.displayText}`
          )
        );
      } else {
        dispatch(setHeader(`Chat with ${sidebarObj.sidebarProps.displayText}`));
      }
    } else {
      dispatch(setHeader(`Chat with ${nice_name}`));
    }
  }

  const findNicenameByProviderName = (providerName: string) => {
    const provider = convoState.providers.find(
      (provider) => provider.name === providerName
    );
    return provider ? provider.options.nice_name : "Provider not found";
  };

  /**
   * Set the conversation ID and model to display, on History List select
   */
  const setConversationFromHistory = (id, model) => {
    if (id && model) {
      // Set the currently selected conversation
      dispatch(setCurrentConversation(id));

      // Update the chosen model in Redux state
      dispatch(setActiveModel(model));

      const sidebarObj = findObjectByParameter(modelData, model);

      // Update the value in the header, see if its modelData obj was correctly entered
      setHeaderName(sidebarObj, model);

      // Close the history page if open
      setShowCompleteHistory(false);
    }
  };

  /**
   * When the model changesd in our Redux State, update the header
   * Notes:
   * - if 'customPrefix: false' in the sidebarObj then use 'customPrefix' + 'displayText'
   * - else use Chat with 'displayText'
   */
  useEffect(() => {
    if (
      convoState.activeModel &&
      convoState.providers &&
      convoState.providers.length > 0
    ) {
      const nice_name = findNicenameByProviderName(convoState.activeModel);
      const sidebarObj = findObjectByParameter(
        modelData,
        convoState.activeModel
      );

      // Update the value in the header, see if its modelData obj was correctly entered
      setHeaderName(sidebarObj, nice_name);

      // // Start a new conversation
      // handleStartNewConversation();
    }
  }, [convoState.activeModel, convoState.providers]);

  const { apptheme } = useAppTheme();

  const [workbookData, setWorkbookData] = useState<{ [sheet: string]: any[][] }>({});
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [excelData, setExcelData] = useState<any[][]>([]);

  const processExcelUpload = (file: File) => {
    const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    const fileType = file.type;

  // Validation check
  if (!allowedExtensions.includes(fileExtension) || !allowedMimeTypes.includes(fileType)) {
    alert("Invalid file type! Please upload an Excel or CSV file.");
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    const data = new Uint8Array(e.target?.result as ArrayBuffer);
    const workbook = XLSX.read(data, { type: "array" });

    const parsedData: { [sheet: string]: any[][] } = {};
    workbook.SheetNames.forEach((sheetName) => {
      const ws = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
      parsedData[sheetName] = json;
    });

    setWorkbookData(parsedData);
    setSheetNames(workbook.SheetNames);
    setSelectedSheet(workbook.SheetNames[0]);
    setExcelData(parsedData[workbook.SheetNames[0]]);
  };
  reader.readAsArrayBuffer(file);
};


  return (
    <PreviewProvider>
      <div className="flex h-full overflow-hidden">
        {/* Main chat area - responsive width based on preview presence */}
        <MainChatArea
          apptheme={apptheme}
          handleStartNewConversation={handleStartNewConversation}
          setConversationFromHistory={setConversationFromHistory}
          setShowCompleteHistory={setShowCompleteHistory}
          showCompleteHistory={showCompleteHistory}
          socket={socket}
          onExcelUpload={processExcelUpload}
          convoState={convoState}
        />

        {/* Preview pane - will only render when previewContent exists */}
        <PreviewPane
          workbookData={workbookData}
          setWorkbookData={setWorkbookData}
          sheetNames={sheetNames}
          setSheetNames={setSheetNames}
          selectedSheet={selectedSheet}
          setSelectedSheet={setSelectedSheet}
          excelData={excelData}
          setExcelData={setExcelData}
        />
      </div>
    </PreviewProvider>
  );
}
